import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import { reset, resetSelectedSlices } from "./slice/reset";
import actionReducer from "./slice/actions";
import configsReducer from "./slice/configs";
import customModulesReducer, { resetCustomModuleSlice } from "./slice/custom-modules";
import dealsReducer from "./slice/deals";
import filesReducer from "./slice/files";
import notesReducer from "./slice/notes";
import enquiriesReducer from "./slice/enquiries";
import permissionsReducer from "./slice/permissions";
import primarySourcesReducer from "./slice/primary-sources";
import requestsReducer from "./slice/requests";
import sourcesReducer from "./slice/sources";
import stagesReducer, { resetStageSlice } from "./slice/stages";
import statusesReducer, { resetStatusSlice } from "./slice/statuses";
import tasksReducer, { resetTaskSlice } from "./slice/tasks";
import userGroupsReducer, { resetUserGroupsSlice } from "./slice/user-groups";
import userRolesReducer from "./slice/user-roles";
import useSettingReducer from "./slice/user-settings";
import usersReducer, { resetUserSlice } from "./slice/users";
import userReducer from "./slice/user";
import activitiesReducer, { resetActivitySlice } from "./slice/activities";
import { leadReducer } from "./slice/leads";
import { marketingReducer } from "./slice/marketingInfo";
import { notificationReducer } from "./slice/notification";
import { organizationReducer } from "./slice/organization";
import { externalIntegrationConfigReducer } from "./slice/external-integration-config";
import invitationReducer from "./slice/invitationSlice";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import { resetLeadSlice } from "./slice/leads/leads.reducer";
import { externalIntegrationMappersReducer } from "./slice/external-integration-mappings/external-integration-mappers.reducer";
import webhookLogReducer from "./slice/webhook-logs/webhook-logs.reducer"
import reportsReducer from "./slice/reports";
import { workflowsReducer } from "./slice/workflows";
import { approvalRequestsReducer } from "./slice/approval-requests";
import { bootstrapReducer } from "./slice/bootstrap";
const appReducer: any = combineReducers({
  actions: actionReducer,
  configs: configsReducer,
  leads: leadReducer,
  bootstrap: bootstrapReducer,
  customModules: customModulesReducer,
  deals: dealsReducer,
  files: filesReducer,
  notes: notesReducer,
  enquiries: enquiriesReducer,
  permissions: permissionsReducer,
  primarySources: primarySourcesReducer,
  requests: requestsReducer,
  sources: sourcesReducer,
  stages: stagesReducer,
  statuses: statusesReducer,
  tasks: tasksReducer,
  userGroups: userGroupsReducer,
  userRoles: userRolesReducer,
  users: usersReducer,
  userSettings: useSettingReducer,
  marketing: marketingReducer,
  notification: notificationReducer,
  user: userReducer,
  organization: organizationReducer,
  activities: activitiesReducer,
  externalIntegrationConfig: externalIntegrationConfigReducer,
  invitations: invitationReducer,
  externalIntegrationMappings: externalIntegrationMappersReducer,
  webhookLogs: webhookLogReducer,
  reports: reportsReducer,
  workflows: workflowsReducer,
  approvalRequests: approvalRequestsReducer,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === reset.type) {
    // Reset the entire state to initial state
    state = undefined;
  }
  if (action.type === resetSelectedSlices.type) {
    const keysToReset = action.payload; // ['leads', 'marketing']
    const newState = { ...state };
    for (const key of keysToReset) {
      if (newState.hasOwnProperty(key)) {
        newState[key] = undefined; // Redux Toolkit re-inits with initialState
      }
    }
    return appReducer(newState, action);
  }

  return appReducer(state, action);
};

// Fix for Next.js: use browser storage only on client
const createNoopStorage = () => {
  return {
    getItem(_key: string) {
      return Promise.resolve(null);
    },
    setItem(_key: string) {
      return Promise.resolve();
    },
    removeItem(_key: string) {
      return Promise.resolve();
    },
  };
};

const storage =
  typeof window !== "undefined"
    ? createWebStorage("local") // ? createWebStorage("session") // or "local"
    : createNoopStorage();

const persistConfig = {
  key: "root",
  storage: storage, // Use the custom storage here
  whitelist: [
    "leads",
    "actions",
    "users",
    "configs",
    "customModules",
    "primarySources",
    "requests",
    "sources",
    "stages",
    "statuses",
    "userGroups",
    "userRoles",
    "marketing",
    "userSettings",
    "organization",
    "tasks",
    // "activities",
    "externalIntegrationConfig",
    "externalIntegrationMappings",
    "webhookLogs",
    "reports",
    "workflows",
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);


export const makeStore = () => {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
  })
}
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"],
        ignoredPaths: [],
      },
    }),
});

export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);

// Function to clear all data and perform logout
export const clearAllDataAndLogout = async (dispatch: AppDispatch) => {
  // Purge and flush redux-persist storage
  await persistor.purge();
  await persistor.flush();

  // Reset Redux state
  dispatch(reset());

  // Clear all browser storage as safety measure
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem("persist:root");
    } catch (e) {
      console.warn("Could not clear persist:root from localStorage", e);
    }
  }
};

export const clearOrganizationLevelData = (dispatch: AppDispatch) => {
  dispatch(resetLeadSlice());
  dispatch(resetUserSlice())
  dispatch(resetUserGroupsSlice())
  dispatch(resetCustomModuleSlice())
  dispatch(resetStageSlice())
  dispatch(resetStatusSlice())
  dispatch(resetActivitySlice())
  dispatch(resetTaskSlice())
  // await persistor.purge();
  // dispatch(resetSelectedSlices(["leads", "users", "customModules", "primarySources", "requests", "sources", "stages", "statuses", "userGroups", "userRoles", "marketing", "userSettings", "tasks", "activities", "externalIntegrationConfig"]));
};

export default store;
